import { useState, useEffect } from 'react';
import './App.css';
import MonadSigil from './landing/MonadSigil';
import MonadFlow from './landing/MonadFlow';
import DoesAnim from './landing/DoesAnim';
import monadLogo from './base_logo_light.png';

// Diverse goals that rotate under the slogan to show MONAD spans all of biology.
const HERO_GOALS = [
  'map a tumor’s driver mutations',
  'design a CRISPR knockout screen',
  'find why a drug failed in phase II',
  'trace a broken metabolic pathway',
  'annotate a single-cell atlas',
  'rank targets for a rare disease',
  'explain a GWAS association',
  'model a protein variant’s effect',
  'spot a biomarker in your cohort',
  'reposition an old drug',
];

// Per-benchmark leaderboards. Each row is a model (or human baseline) we measure
// against, with its score; MONAD's two models are flagged with `me`. Rows are
// pre-sorted high to low. All scores are 0-100 so they map straight to bar width.
const BENCHMARKS = [
  {
    name: 'GPQA Diamond',
    measures: 'science reasoning',
    rows: [
      { m: 'Phenome-1 Pro', s: 98, me: true },
      { m: 'GPT-5.4', s: 97 },
      { m: 'Phenome-1', s: 95, me: true },
      { m: 'Claude Opus 4.6', s: 95 },
      { m: 'Gemini 3.1 Pro', s: 93 },
    ],
  },
  {
    name: 'BiomniBench',
    measures: 'research trajectories',
    rows: [
      { m: 'Phenome-1 Pro', s: 78, me: true },
      { m: 'Claude Opus 4.7', s: 73 },
      { m: 'GPT-5.4', s: 69 },
      { m: 'Phenome-1', s: 68, me: true },
      { m: 'Claude Sonnet 4.6', s: 66 },
      { m: 'Gemini 3.1 Pro', s: 50 },
    ],
  },
  {
    name: 'GenoTEX',
    measures: 'gene-trait F1',
    rows: [
      { m: 'Phenome-1 Pro', s: 74, me: true },
      { m: 'Human experts', s: 72, human: true },
      { m: 'Phenome-1', s: 64, me: true },
      { m: 'GenoMAS', s: 60 },
      { m: 'GenoAgent (o1)', s: 44 },
    ],
  },
  {
    name: 'BixBench',
    measures: 'open-ended analysis',
    rows: [
      { m: 'Phenome-1 Pro', s: 61, me: true },
      { m: 'Phenome-1', s: 54, me: true },
      { m: 'Biomni Lab', s: 52 },
      { m: 'K-Dense Analyst', s: 34 },
      { m: 'GPT-5', s: 23 },
    ],
  },
  {
    name: 'LAB-Bench · LitQA2',
    measures: 'literature precision',
    rows: [
      { m: 'Phenome-1 Pro', s: 89, me: true },
      { m: 'PaperQA2', s: 85 },
      { m: 'Phenome-1', s: 84, me: true },
      { m: 'Human experts', s: 74, human: true },
      { m: 'GPT-4o', s: 46 },
    ],
  },
];

function BenchBreakdown() {
  return (
    <div className="WSMONAD-bb">
      <div className="WSMONAD-bb-head">
        <h3>Benchmarks</h3>
        <p>
          Phenome-1 Pro tops every benchmark we run, and Phenome-1 holds frontier parity at a
          fraction of the compute. Competitor figures are the best published results to date.
        </p>
      </div>
      <div className="WSMONAD-bb-grid">
        {BENCHMARKS.map((b) => (
          <div className="WSMONAD-bb-card" key={b.name}>
            <div className="WSMONAD-bb-cardhead">
              <span className="WSMONAD-bb-name">{b.name}</span>
              <span className="WSMONAD-bb-measures">{b.measures}</span>
            </div>
            <div className="WSMONAD-bb-rows">
              {b.rows.map((r) => (
                <div
                  className="WSMONAD-bb-row"
                  key={r.m}
                  data-me={r.me ? 'true' : undefined}
                  data-human={r.human ? 'true' : undefined}
                >
                  <span className="WSMONAD-bb-model">{r.m}</span>
                  <div className="WSMONAD-bb-track">
                    <div className="WSMONAD-bb-fill" style={{ width: `${r.s}%` }} />
                  </div>
                  <span className="WSMONAD-bb-score">{r.s}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoalCycler() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = setInterval(() => setI((v) => (v + 1) % HERO_GOALS.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="WSMONAD-goalline" aria-hidden="true">
      <span className="WSMONAD-goalline-lead">Ask it to</span>
      <span className="WSMONAD-goalline-rot" key={i}>
        {HERO_GOALS[i]}
      </span>
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Reveal each section as it scrolls into view. The page scrolls inside
  // .WSMONAD-root (its own scroll container), so the observer roots on it.
  useEffect(() => {
    const scroller = document.querySelector('.WSMONAD-root');
    if (!scroller) return undefined;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The page scrolls inside .WSMONAD-root, so in-page anchor links must scroll
    // that container (smoothly) rather than the document. Logo (href="/") and
    // external links are left alone.
    const onAnchorClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const top = Math.max(
        0,
        scroller.scrollTop +
          target.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top -
          16,
      );
      scroller.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    };
    scroller.addEventListener('click', onAnchorClick);

    // Reveal each section as it scrolls into view.
    let io;
    if (!reduce && typeof IntersectionObserver !== 'undefined') {
      const targets = Array.from(
        scroller.querySelectorAll(
          '.WSMONAD-monad-flow, .WSMONAD-section-head, .WSMONAD-does-row, .WSMONAD-engine-card, .WSMONAD-bb, .WSMONAD-cta-strip',
        ),
      );
      targets.forEach((el) => el.classList.add('WSMONAD-reveal'));
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('WSMONAD-shown');
              io.unobserve(e.target);
            }
          });
        },
        { root: scroller, rootMargin: '0px 0px -10% 0px', threshold: 0.1 },
      );
      targets.forEach((el) => io.observe(el));
    }

    return () => {
      scroller.removeEventListener('click', onAnchorClick);
      if (io) io.disconnect();
    };
  }, []);

  return (
    <div className="WSMONAD-root">
      <div className="WSMONAD-page">
      {/* Register */}
      <header className="WSMONAD-register">
        <div className="WSMONAD-left">
          <a href="/" className="WSMONAD-logo-link" aria-label="Home">
            <img className="WSMONAD-logo" src={monadLogo} alt="MONAD" />
          </a>
        </div>
        <nav className="WSMONAD-center WSMONAD-nav">
          <a href="#does">Capabilities</a>
          <a href="#engine">Engine</a>
          <a href="#flow">In Motion</a>
          <a href="#access">Access</a>
        </nav>
        <div className="WSMONAD-right">
          <a href="#access">Request access ↗</a>
        </div>
        <button
          className="WSMONAD-burger"
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      {/* Mobile slide-out menu */}
      <div
        className={`WSMONAD-scrim${menuOpen ? ' WSMONAD-open' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
      <nav className={`WSMONAD-drawer${menuOpen ? ' WSMONAD-open' : ''}`} aria-label="Menu">
        <button className="WSMONAD-drawer-x" type="button" aria-label="Close menu" onClick={closeMenu}>
          ✕
        </button>
        <a href="#does" onClick={closeMenu}>Capabilities</a>
        <a href="#engine" onClick={closeMenu}>Engine</a>
        <a href="#flow" onClick={closeMenu}>In Motion</a>
        <a href="#access" onClick={closeMenu}>Access</a>
        <a href="#access" className="WSMONAD-drawer-cta" onClick={closeMenu}>
          Request access ↗
        </a>
      </nav>

      {/* Hero */}
      <div className="WSMONAD-hero" style={{ borderBottom: '1px solid var(--rule)' }}>
        <div className="WSMONAD-hero-frame" aria-hidden="true">
          <div className="WSMONAD-reg WSMONAD-tl">
            <svg viewBox="0 0 22 22"><path d="M 0 6 H 12 M 6 0 V 12" /></svg>
          </div>
          <div className="WSMONAD-reg WSMONAD-tr">
            <svg viewBox="0 0 22 22"><path d="M 0 6 H 12 M 6 0 V 12" /></svg>
          </div>
          <div className="WSMONAD-reg WSMONAD-bl">
            <svg viewBox="0 0 22 22"><path d="M 0 6 H 12 M 6 0 V 12" /></svg>
          </div>
          <div className="WSMONAD-reg WSMONAD-br">
            <svg viewBox="0 0 22 22"><path d="M 0 6 H 12 M 6 0 V 12" /></svg>
          </div>
        </div>

        <div className="WSMONAD-hero-col">
          <div className="WSMONAD-hero-eyebrow">Multi-Omics Network And Discovery</div>
          <h1 className="WSMONAD-slogan">
            <span className="WSMONAD-line">Autonomous discovery,</span>
            <span className="WSMONAD-line">
              in any <span className="WSMONAD-indigo">biological domain</span>.
            </span>
          </h1>
          <GoalCycler />
          <p className="WSMONAD-lede">
            Give it a goal. MONAD summons operators, pulls from genomic, transcriptomic, proteomic,
            single-cell and clinical databases{' '}
            <span className="WSMONAD-lede-or">
              (or yours
              <svg className="WSMONAD-up-ic" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 11V3M8 3L4.5 6.5M8 3l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 11.5v1.5h10v-1.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              )
            </span>
            , and keeps working until <span className="WSMONAD-indigo">it is achieved</span>.
          </p>
          <div className="WSMONAD-ctas">
            <a href="#access" className="WSMONAD-btn">
              Try MONAD <span className="WSMONAD-arr">↗</span>
            </a>
            <a href="#flow" className="WSMONAD-btn WSMONAD-ghost">
              See it in motion
            </a>
          </div>
        </div>

        <div className="WSMONAD-hero-sigil">
          <MonadSigil svgId="monad-sigil" N={130} R={84} />
        </div>
      </div>

      {/* The work: composer → spatial workspace */}
      <MonadFlow />

      {/* What MONAD does */}
      <div id="does" className="WSMONAD-section">
        <div className="WSMONAD-section-head">
          <div className="WSMONAD-section-num">
            01 <em>/ Capabilities</em>
          </div>
          <div>
            <h2 className="WSMONAD-section-title">
              What MONAD <span className="WSMONAD-it">actually does</span>.
            </h2>
            <p className="WSMONAD-section-deck">
              Give it an objective in any part of biology. MONAD builds the workspace, gathers the
              data, and runs the investigation until the goal is met, and you can steer it the whole
              way.
            </p>
          </div>
        </div>

        <div className="WSMONAD-does">
          <div className="WSMONAD-spacer" />
          <div className="WSMONAD-list">
            <div className="WSMONAD-does-row">
              <div className="WSMONAD-num">
                <span className="WSMONAD-num-n">01</span>
                <span className="WSMONAD-num-t">OBJECTIVE</span>
              </div>
              <div>
                <h3>
                  Any biological objective, written in <span className="WSMONAD-it">plain language</span>.
                </h3>
                <p>
                  Start with a goal: identify disease drivers, interpret variants, build a cohort,
                  compare tissues, evaluate a target, benchmark a compound, or reproduce a finding.
                  You can upload files, select data from connected cloud storage, or begin with no
                  external data at all. MONAD already connects to 82 curated biomedical databases
                  spanning genomics, variants, expression, single-cell biology, proteins, structures,
                  pathways, interactions, drugs, clinical evidence, epigenomics, immunology,
                  microbiome data, metabolomics, and literature.
                </p>
              </div>
              <div className="WSMONAD-does-anim" data-anim="objective" aria-hidden="true">
                <DoesAnim phase="objective" />
              </div>
            </div>

            <div className="WSMONAD-does-row">
              <div className="WSMONAD-num">
                <span className="WSMONAD-num-n">02</span>
                <span className="WSMONAD-num-t">DOMAIN</span>
              </div>
              <div>
                <h3>
                  A <span className="WSMONAD-it">private computational workspace</span> is created for
                  the investigation.
                </h3>
                <p>
                  Once you submit the objective, MONAD provisions an isolated domain with dedicated
                  CPU, GPU, and storage, and mounts in the data it needs. This is not a chat thread.
                  It is a live computational environment where MONAD writes and executes its own
                  code, and every table, figure, script, and result lands as a real file you can open
                  and download.
                </p>
              </div>
              <div className="WSMONAD-does-anim" data-anim="domain" aria-hidden="true">
                <DoesAnim phase="domain" />
              </div>
            </div>

            <div className="WSMONAD-does-row">
              <div className="WSMONAD-num">
                <span className="WSMONAD-num-n">03</span>
                <span className="WSMONAD-num-t">ORCHESTRATION</span>
              </div>
              <div>
                <h3>
                  A team of <span className="WSMONAD-it">operators</span> takes on the objective.
                </h3>
                <p>
                  MONAD breaks the objective into executable work, selects the relevant databases and
                  tools, and coordinates autonomous operators across the investigation. Operators
                  query sources, write code, launch analyses, compare outputs, inspect evidence, and
                  pass results between each other. You can steer the investigation while it is
                  running, refine the direction, or redirect the work without restarting from zero.
                </p>
              </div>
              <div className="WSMONAD-does-anim" data-anim="orchestration" aria-hidden="true">
                <DoesAnim phase="orchestration" />
              </div>
            </div>

            <div className="WSMONAD-does-row">
              <div className="WSMONAD-num">
                <span className="WSMONAD-num-n">04</span>
                <span className="WSMONAD-num-t">RESOLUTION</span>
              </div>
              <div>
                <h3>
                  The output matches the <span className="WSMONAD-it">objective</span>, not a fixed
                  template.
                </h3>
                <p>
                  MONAD keeps going through analysis, verification, replanning, and synthesis until
                  the objective is met. The result might be a ranked target list, an annotated cohort,
                  a variant interpretation, a drug-target comparison, a cross-omics analysis, a
                  literature-grounded report, figures, or tables, and it all comes back as a
                  downloadable artifact folder, the complete workspace, where every claim traces to
                  its data source and the steps that produced it.
                </p>
              </div>
              <div className="WSMONAD-does-anim" data-anim="resolution" aria-hidden="true">
                <DoesAnim phase="resolution" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Engine */}
      <div id="engine" className="WSMONAD-section">
        <div className="WSMONAD-section-head">
          <div className="WSMONAD-section-num">
            02 <em>/ Engine</em>
          </div>
          <div>
            <h2 className="WSMONAD-section-title">
              Two LLMs, <span className="WSMONAD-it">trained for biology</span>.
            </h2>
            <p className="WSMONAD-section-deck">
              Phenome-1 and Phenome-1 Pro are MONAD's proprietary models. They are not generalist
              foundation models with a biology prompt. They were trained from the ground up to plan,
              dispatch, and reason over multi-omics evidence.
            </p>
          </div>
        </div>

        <div className="WSMONAD-engine-grid">
          <div className="WSMONAD-engine-card">
            <div className="WSMONAD-tag">Standard · Phenome-1</div>
            <h3>
              Phenome<span className="WSMONAD-it">-1</span>
            </h3>
            <p className="WSMONAD-role">
              The orchestrator. It works from compact summaries to plan each step and coordinate
              operators across the run.
            </p>
            <div className="WSMONAD-specs">
              <div className="WSMONAD-spec">
                <span>Role</span>
                <b>Orchestrator</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Active params</span>
                <b>3B / 35B MoE</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Context</span>
                <b>256k</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Best for</span>
                <b>Routine runs</b>
              </div>
            </div>
          </div>

          <div className="WSMONAD-engine-card">
            <div className="WSMONAD-pro-badge">Pro</div>
            <div className="WSMONAD-tag">Larger · Phenome-1 Pro</div>
            <h3>
              Phenome<span className="WSMONAD-it">-1 Pro</span>
            </h3>
            <p className="WSMONAD-role">
              The deep reasoner. Longer reasoning chains and broader recall, reserved for the
              hardest questions.
            </p>
            <div className="WSMONAD-specs">
              <div className="WSMONAD-spec">
                <span>Role</span>
                <b>Deep reasoning</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Active params</span>
                <b>32B / 355B MoE</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Context</span>
                <b>256k</b>
              </div>
              <div className="WSMONAD-spec">
                <span>Best for</span>
                <b>Novel hypotheses</b>
              </div>
            </div>
          </div>
        </div>

        <BenchBreakdown />
      </div>

      {/* CTA */}
      <div id="access" className="WSMONAD-cta-strip" style={{ borderBottom: '1px solid var(--rule)' }}>
        <div>
          <div className="WSMONAD-eyebrow">Early access</div>
          <h2>
            Put MONAD to work on your{' '}
            <span className="WSMONAD-indigo">research</span>.
          </h2>
          <p className="WSMONAD-copy">
            MONAD is in early access for academic and industry research teams. Tell us what you'd
            run first, and we'll follow up.
          </p>
        </div>
        <div className="WSMONAD-form">
          <div className="WSMONAD-label">Request early access</div>
          <div className="WSMONAD-row">
            <input type="email" placeholder="you@lab.edu" />
            <button className="WSMONAD-btn" type="button">
              Send <span className="WSMONAD-arr">↗</span>
            </button>
          </div>
        </div>
      </div>

      </div>
    </div>
  );
}

export default App;
